import React from "react";
import ReactDOM from "react-dom";
import L from "leaflet";
import "leaflet-hash";
import "leaflet-editable";
import marker from "../map/marker";
import Popup from "./popup";
import geojsonRewind from "geojson-rewind";
import simplestyle from "./simplestyle";
import { BenchMark } from "../lib/devProfiler"
import iconRetinaUrl from "../../css/marker-icon-2x.png";
import iconUrl from "../../css/marker-icon.png";
import shadowUrl from "../../css/marker-shadow.png";
import { checkHotspotType } from "./utils"

const polygon = <path d="M15 6l8.56 6.219-3.27 10.062H9.71L6.44 12.22z" />;
const line = <path d="M6 7l8.31 3.99v7.822l8.31 4.746" />;
const rectangle = <path d="M7.5 7.5h15v15h-15z" />;
const point = (
  <path d="M15 24.152L7 13.015c-.747-4.83 1.92-7.246 8-7.246s8.747 2.415 8 7.246l-8 11.137z" />
);
const circle = <path d="M 10 10 A 2 2 0 1 1 20 20 A 2 2 0 1 1 10 10" />

L.Marker.prototype.options.icon = new L.Icon({
  iconUrl,
  iconRetinaUrl,
  shadowUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41]
});

function _parse_query_string(query) {
  var vars = query.split("&");
  var query_string = {};
  for (var i = 0; i < vars.length; i++) {
    var pair = vars[i].split("=");
    var key = decodeURIComponent(pair[0]);
    var value = decodeURIComponent(pair[1]);
    // If first entry with this name
    if (typeof query_string[key] === "undefined") {
      query_string[key] = decodeURIComponent(value);
      // If second entry with this name
    } else if (typeof query_string[key] === "string") {
      var arr = [query_string[key], decodeURIComponent(value)];
      query_string[key] = arr;
      // If third or later entry with this name
    } else {
      query_string[key].push(decodeURIComponent(value));
    }
  }
  return query_string;
}

export default class Map extends React.Component {
  constructor(props) {
    super();
    this.mapRef = React.createRef();
  }

  componentWillReceiveProps(nextProps) {
    const {
      map: {
        editTools: { featuresLayer }
      },
      map: {
        editTools: { options: { backgroundLayer } }
      },
      map
    } = this.state;

    // this is kinda hacky, but it works
    // --------------------------------
    var maxId = 1; // this starts at 1 for some stupid reason
    nextProps.geojson.features.forEach(feature => {
      //console.log(`${feature.properties.hotspotId} = ${maxId}`);
      if (feature.properties.hotspotId == undefined) {
        feature.properties.hotspotId = "" + maxId;
      } else {
        maxId = parseInt(feature.properties.hotspotId) + 1;
      }
    });
    // --------------------------------

    let mapExist = false;
    let mapUrlExist = (nextProps.metadata.mapImageOverlayUrl !== undefined
      && nextProps.metadata.mapImageOverlayUrl !== "");


    if (backgroundLayer.getLayers().shift() !== undefined) {
      if (backgroundLayer.getLayers().shift()._url !== undefined) {
        mapExist = true;
      }
    }

    if ((mapExist && !mapUrlExist) || (!mapExist && mapUrlExist)) {
      backgroundLayer.clearLayers();
      if (mapUrlExist) {
        this.addMapImage();
      }

      featuresLayer.eachLayer((layer) => {
        if (layer._latlng !== undefined) {
          if (layer.feature) {
            let colour;
            const selected = checkHotspotType(layer.feature.properties);
            switch (selected) {
              case "standard": colour = "#3388ff"; break;
              case "instructional": colour = "#111111"; break;
              case "background": colour = "#aaaaaa"; break;
              default: colour = "#33dd33"; break;
            }

            let c = L.circle(layer._latlng, { radius: layer.feature.properties.radius || "20", interactive: false, color: colour });
            c.markerId = layer._leaflet_id;
            backgroundLayer.addLayer(c);
          }
        }
      });
    }


    if (nextProps.openPopupId !== false) {
      featuresLayer.eachLayer((layer) => {
        if (layer.feature !== undefined) {

          if (layer.feature.properties.hotspotId == nextProps.openPopupId) {
            const popup = L.popup(
              {
                closeButton: false,
                maxHeight: 450,
                autoPanPadding: [5, 45],
                classname: "geojsonnet-feature"
              },
              layer
            );
            if (layer.getLatLng !== undefined) {
              popup.setLatLng(layer.getLatLng())
            } else {
              popup.setLatLng(layer.getCenter())
            }
            popup.setContent(this.makePopup(layer))
            popup.openOn(map)
            layer.off("popupclose popupopen")
            layer.on("popupclose popupopen", (event) => {

              if (event.type === "popupopen") {
                this.props.openPopup(layer.feature.properties.hotspotId)
              } else {

                layer.setTooltipContent(layer.feature.properties.title || "No Title");
                this.props.openPopup(-1)
                // -1 means a popup has been closed and should remove highlighting,
                // false means a new popup should not be opened on next load 
              }
            })
            layer.off("remove", () => { map.closePopup() })
            layer.on("remove", () => { map.closePopup() })
          }

        }
        nextProps.openPopup(false)
      })
    }
  }

  componentDidMount() {
    const featuresLayer = L.layerGroup();
    const backgroundLayer = L.layerGroup();
    let map = new L.Map(this.mapRef.current, {
      editable: true,
      editOptions: {
        featuresLayer,
        backgroundLayer
      }
    });
    const { layer } = this.props;
    L.control
      .scale()
      .setPosition("bottomright")
      .addTo(map);
    map.zoomControl.setPosition("topright");
    map.setView([20, 0], 2);

    const params = _parse_query_string(window.location.search.substring(1));
    if (params.maxZoom !== undefined) {
      map.setMaxZoom(params.maxZoom)
    }

    L.hash(map);
    const metric =
      navigator.language !== "en-us" && navigator.language !== "en-US";


    map.attributionControl.setPrefix("");

    const baseLayerGroup = L.layerGroup().addTo(map);
    this.props.layers.find(({ id }) => id === layer).layer.addTo(baseLayerGroup);
    // map.on("editable:drawing:commit", this.updateFromMap);

    L.EditControl = L.Control.extend({
      options: {
        position: "topleft",
        callback: null,
        kind: ""
      },

      onAdd: function (map) {
        var container = L.DomUtil.create(
          "div",
          "leaflet-control leaflet-bar bg-white black hover-bg-purple hover-white"
        ),
          link = L.DomUtil.create("a", "", container);

        link.href = "#";
        link.title = "Create a new " + this.options.kind;
        const iconContainer = link.appendChild(document.createElement("div"));
        ReactDOM.render(
          <svg
            stroke="currentColor"
            strokeWidth="2.5"
            fill="none"
            width="30"
            height="30"
          >
            {this.options.icon}
          </svg>,
          iconContainer
        );
        L.DomEvent.on(link, "click", L.DomEvent.stop).on(
          link,
          "click",
          function () {
            window.LAYER = this.options.callback.call(map.editTools);
          },
          this
        );

        return container;
      }
    });

    L.NewLineControl = L.EditControl.extend({
      options: {
        position: "topright",
        callback: map.editTools.startPolyline,
        kind: "line",
        icon: line
      }
    });

    L.NewPolygonControl = L.EditControl.extend({
      options: {
        position: "topright",
        callback: map.editTools.startPolygon,
        kind: "polygon",
        icon: polygon
      }
    });

    L.NewMarkerControl = L.EditControl.extend({
      options: {
        position: "topright",
        callback: map.editTools.startMarker,
        kind: "marker",
        icon: point
      }
    });

    L.NewRectangleControl = L.EditControl.extend({
      options: {
        position: "topright",
        callback: map.editTools.startRectangle,
        kind: "rectangle",
        icon: rectangle
      }
    });

    L.NewCircleControl = L.EditControl.extend({
      options: {
        position: "topright",
        callback: map.editTools.startCircle,
        kind: "circle",
        icon: circle
      }
    });

    // map.addControl(new L.NewMarkerControl());
    // map.addControl(new L.NewLineControl());
    // map.addControl(new L.NewPolygonControl());
    map.addControl(new L.NewRectangleControl());
    map.addControl(new L.NewCircleControl());
    map.addLayer(backgroundLayer);
    map.addLayer(featuresLayer);

    map
      .on("editable:dragend", this.updateFromMap)
      .on("editable:created", this.updateFromMap)
      .on("editable:drawing:commit", this.ensureMapIsGeoJSON)
      .on("editable:editing", this.updateFromMap);

    this.setState({
      map,
      baseLayerGroup
    });
  };

  bindLayerPopup = layer => {
    const { map } = this.state;
    if ("bindPopup" in layer) {
      
      layer.off("click")
      layer.on("click", (event) => {
        const popup = L.popup(
          {
            closeButton: false,
            maxHeight: 450,
            autoPanPadding: [5, 45],
            classname: "geojsonnet-feature"
          },
          layer
        );
        if (layer.getLatLng !== undefined) {
          popup.setLatLng(layer.getLatLng())
        } else {
          popup.setLatLng(layer.getCenter())
        }

        layer.off("popupclose popupopen")
        layer.on("popupclose popupopen", (event) => {

          if (event.type === "popupopen") {
            this.props.openPopup(layer.feature.properties.hotspotId)

          } else {
            layer.setTooltipContent(layer.feature.properties.title || "No Title");
            this.props.openPopup(-1)
          }
        })
        popup.setContent(this.makePopup(layer))
        popup.openOn(map)
      });
      layer.off("remove", () => { map.closePopup() })
      layer.on("remove", () => { map.closePopup() })
    }
  };

  addMapImage = () => {
    const {
      map: {
        editTools: { options: { backgroundLayer } }
      },
      map
    } = this.state;
    const { setMetadata, metadata } = this.props;

    const benchMark = new BenchMark("addMapImage");
    benchMark.startTimer()
    if (metadata !== undefined) {
      if (metadata.imageBaseUrl !== undefined && metadata.mapImageOverlayUrl !== undefined) {
        const image = metadata.imageBaseUrl + metadata.mapImageOverlayUrl;
        const getLatLng = () => {
          if (metadata.mapImageBounds !== undefined) {
            let bounds = metadata.mapImageBounds.trim().split(" ");
            for (const num in bounds) {

              let latlng = bounds[num].split(",");
              for (const coord in latlng) {
                latlng[coord] = parseFloat(latlng[coord]);
              }
              // need to flip the latlngs because it's stored as lnglat
              latlng.reverse();
              bounds[num] = latlng;
            }
            return bounds;
          } else {
            let bounds = map.getBounds()
            bounds = [[bounds.getNorthWest().lat, bounds.getNorthWest().lng], [bounds.getSouthEast().lat, bounds.getSouthEast().lng]]
            let meta = metadata;
            meta.mapImageBounds = `${bounds[0][1]},${bounds[0][0]} ${bounds[1][1]},${bounds[1][0]}`;
            setMetadata(meta);
            return bounds;
          }
        }
        let bounds = getLatLng();
        const centre = [(bounds[0][0] + bounds[1][0]) / 2, (bounds[0][1] + bounds[1][1]) / 2]
        if (map.getZoom() < 4) {
          map.setView(centre, 17);
        }
        let mapOverlay = L.imageOverlay(image, bounds, { opacity: 0.7 });

        benchMark.logEvent("mapOverlay")

        // this adds the ability to change the map image position within the editor

        const tl = L.marker(bounds[0], { draggable: true })
          .on("drag", (event) => {
            let bounds = getLatLng();
            mapOverlay.setBounds([event.latlng, bounds[1]])
          })
          .on("dragend", (event) => {
            let bounds = getLatLng();
            let eLatlngs = event.target.getLatLng();
            let latlng = eLatlngs.lng.toFixed(5).toString() + "," +
              eLatlngs.lat.toFixed(5).toString() + " " +
              bounds[1][1].toString() + "," +
              bounds[1][0].toString();
            let meta = metadata;
            meta.mapImageBounds = latlng;
            setMetadata(meta);
          });

        const br = L.marker(bounds[1], { draggable: true })
          .on("drag", (event) => {
            let bounds = getLatLng();
            mapOverlay.setBounds([bounds[0], event.latlng])
          })
          .on("dragend", (event) => {
            let bounds = getLatLng();
            let eLatlngs = event.target.getLatLng();
            let latlng =
              bounds[0][1].toString() + "," +
              bounds[0][0].toString() + " " +
              eLatlngs.lng.toFixed(5).toString() + "," +
              eLatlngs.lat.toFixed(5).toString();
            let meta = metadata;
            meta.mapImageBounds = latlng;
            setMetadata(meta);
          });

        backgroundLayer.addLayer(mapOverlay)
        backgroundLayer.addLayer(tl);
        backgroundLayer.addLayer(br);
        benchMark.finishTimer();
      }
    }
  };

  popupRemoveLayer = layer => {
    if (confirm("This will PERMANENTLY DELETE the hotspot.\nDo you want to proceed?")) {
      const { map: { editTools: { featuresLayer } } } = this.state;
      featuresLayer.removeLayer(layer);
      this.updateFromMap(); // we need to remove the background circle as well.
    }
  };

  editProperties = (update, layer) => {
    layer.feature.properties = update;
    this.updateFromMap();
  };

  editPropertiesWithoutMap = (update, layer) => {
    layer.feature.properties = update;
    this.updateProperties();
  };


  makePopup = layer => {
    const { setGeojson, metadata } = this.props;
    const { map } = this.state;
    const div = document.createElement("div");
    const popup = ReactDOM.render(
      <Popup
        map={map}
        metadata={metadata}
        layer={layer}
        editProperties={(update) => this.editProperties(update, layer)}
        setGeojson={setGeojson}
        popupRemoveLayer={this.popupRemoveLayer}
      />,
      div
    );
    div.className = "ispopup";
    return div;
  };

  ensureMapIsGeoJSON = e => {
    const {
      map: {
        editTools: { featuresLayer }
      },
      map: {
        editTools: { options: { backgroundLayer } }
      }
    } = this.state;

    const benchMark = new BenchMark("ensureMapIsGeoJSON");
    benchMark.startTimer()

    let newCircle = null;
    try {
      if (featuresLayer.getLayers().pop() !== undefined) {
        if (featuresLayer.getLayers().pop().getRadius !== undefined) {
          newCircle = featuresLayer.getLayers().pop()
        }
      }
    } catch (e) {
      alert("An error has occurred.\n" + e)
    }

    const geojson = featuresLayer.toGeoJSON();
    featuresLayer.clearLayers();
    backgroundLayer.clearLayers();
    this.addMapImage();

    L.geoJson(geojson).eachLayer(layer => {
      if (layer.feature.geometry.type !== "Polygon") {
        if (layer.feature.properties.radius === undefined) {
          if (newCircle !== null) {
            layer.feature.properties.radius = newCircle.getRadius();
          }
        }
        let colour;
        const selected = checkHotspotType(layer.feature.properties);
        switch (selected) {
          case "standard": colour = "#3388ff"; break;
          case "instructional": colour = "#111111"; break;
          case "background": colour = "#aaaaaa"; break;
          default: colour = "#33dd33"; break;
        }
        let c = L.circle(layer.getLatLng(), { radius: layer.feature.properties.radius || "20", interactive: false, color: colour });
        c.markerId = layer._leaflet_id;
        backgroundLayer.addLayer(c);
      }

      layer.bindTooltip((l) => {return l.feature.properties.title || "No Title"})

      featuresLayer.addLayer(layer);
      // layer must be added before editing can be enabled.
      layer.enableEdit();
      layer.on("click", this.clickPolygon);
    });
    benchMark.finishTimer()

    this.updateFromMap(e);
  };

  clickPolygon = e => {
    const { target } = e;
    if (
      (e.originalEvent.ctrlKey || e.originalEvent.metaKey) &&
      target.editEnabled()
    ) {
      target.editor.newHole(e.latlng);
    }
  };

  updateProperties = () => {
    const {
      map: {
        editTools: { featuresLayer }
      },
      map: {
        editTools: { options: { backgroundLayer } }
      }
    } = this.state;
    const benchMark = new BenchMark("updateProperties");
    benchMark.startTimer()
    const { setGeojson } = this.props;
    let geojson = geojsonRewind(featuresLayer.toGeoJSON());
    setGeojson(geojson, "map");
    benchMark.finishTimer();
  }

  updateFromMap = () => {
    const {
      map: {
        editTools: { featuresLayer }
      },
      map: {
        editTools: { options: { backgroundLayer } }
      }
    } = this.state;

    const benchMark = new BenchMark("updateFromMap");
    benchMark.startTimer()

    const { setGeojson } = this.props;
    let geojson = geojsonRewind(featuresLayer.toGeoJSON());
    setGeojson(geojson, "map");

    backgroundLayer.eachLayer((layer) => {
      if (layer.markerId !== undefined) {
        const marker = featuresLayer.getLayer(layer.markerId);

        if (marker !== undefined) {
          let radius = parseFloat(marker.feature.properties.radius);
          if (radius === NaN) { radius = 0; }

          let colour;
          const selected = checkHotspotType(marker.feature.properties);
          switch (selected) {
            case "standard": colour = "#3388ff"; break;
            case "instructional": colour = "#111111"; break;
            case "background": colour = "#aaaaaa"; break;
            default: colour = "#33dd33"; break;
          }

          layer.setStyle({ color: colour });
          layer.setLatLng(marker.getLatLng());
          layer.setRadius(radius || "20");

        } else {
          backgroundLayer.removeLayer(layer);
        }
      }
    });
    benchMark.logEvent("backgroundLayer");
    featuresLayer.eachLayer(this.bindLayerPopup);
    benchMark.finishTimer();
  };

  componentDidUpdate(prevProps, prevState) {
    const { baseLayerGroup, map } = this.state;
    const { showPanel, layer, geojson, changeFrom, metadata } = this.props;
    if (prevProps.showPanel !== showPanel) {
      map.invalidateSize();
    }
    if (layer !== prevProps.layer) {
      baseLayerGroup.clearLayers();
      this.props.layers.find(({ id }) => id === layer).layer.addTo(baseLayerGroup);
    }
    if (geojson !== prevProps.geojson && changeFrom !== "map") {
      const {
        map: {
          editTools: { featuresLayer }
        },
        map: {
          editTools: { options: { backgroundLayer } }
        }
      } = this.state;

      featuresLayer.clearLayers();
      backgroundLayer.clearLayers();
      this.addMapImage();

      let maxId = 1;

      L.geoJson(geojson).eachLayer(layer => {
        if (layer.feature !== undefined) {
          if (layer.feature.properties !== undefined) {
            if (layer.feature.properties.hotspotId == undefined) {
              // I think this is broken but I have no way to tell because I don't understand it...
              layer.feature.properties.hotspotId = "" + maxId;
              maxId++;

            } else {
              maxId = parseInt(layer.feature.properties.hotspotId) + 1;
            }
          }
        }
        if (layer._latlng !== undefined) {
          let colour;
          const selected = checkHotspotType(layer.feature.properties);
          switch (selected) {
            case "standard": colour = "#3388ff"; break;
            case "instructional": colour = "#111111"; break;
            case "background": colour = "#aaaaaa"; break;
            default: colour = "#33dd33"; break;
          }

          let c = L.circle(layer._latlng, { radius: layer.feature.properties.radius || "20", interactive: false, color: colour });
          c.markerId = layer._leaflet_id;
          backgroundLayer.addLayer(c);
        }

        layer.bindTooltip((l) => {return l.feature.properties.title || "No Title"})

        featuresLayer.addLayer(layer);
        // layer must be added before editing can be enabled.
        layer.enableEdit();
      });
      featuresLayer.eachLayer(this.bindLayerPopup);
    }
  };

  startLine = () => {
    const { map } = this.state;
    map.editTools.startPolyline();
  };

  startPolygon = () => {
    const { map } = this.state;
    map.editTools.startPolygon();
  };

  startRectangle = () => {
    const { map } = this.state;
    map.editTools.startRectangle();
  };

  startMarker = () => {
    const { map } = this.state;
    map.editTools.startMarker();
  };

  startCircle = () => {
    const { map } = this.state;
    map.editTools.startCircle();
  };

  render() {
    return <div className="flex-auto" ref={this.mapRef} />;
  }
}
